import { useCallback, useEffect, useRef, useState, type DragEvent } from 'react'
import { useUploadMedia } from '@/hooks/queries'
import { cn } from '@/lib/cn'
import type { Media } from '@/models/media'
import { cropImageToFile, type CoverSpec, type CropRect } from '@/lib/cover-spec'
import { TagEditor } from './ui/TagEditor'
import { ImageCropper } from './ui/ImageCropper'

const MAX_UPLOAD_BYTES = 8 * 1024 * 1024

/** Bytes as a person reads them: "1.4 MB", not "1468006". */
function formatBytes(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`
  if (bytes < 1024 * 1024) return `${Math.round(bytes / 1024)} KB`
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`
}

/** True when the crop is the full image, give or take a pixel of rounding. */
function coversWholeImage(
  crop: CropRect,
  image: { width: number; height: number },
  maxWidth: number,
): boolean {
  return (
    crop.x <= 1 &&
    crop.y <= 1 &&
    crop.width >= image.width - 1 &&
    crop.height >= image.height - 1 &&
    image.width <= maxWidth
  )
}

interface Chosen {
  file: File
  preview: string
  width: number
  height: number
}

interface UploadMediaProps {
  onCancel: () => void
  onUploaded: (media: Media) => void
  /** When set, the image is cropped to this shape before upload. */
  coverSpec?: CoverSpec
  /** A file dropped on the library before this screen opened. */
  initialFile?: File
}

/**
 * Adding cover art to the library.
 *
 * The original was a 280px button labelled "Upload" over two generic fields: no
 * drag and drop, no paste, nothing said about what the API would accept, and
 * nothing shown about the file once it was chosen. Rejections went to a toast —
 * which is now a page, so a form that reported "larger than 8 MB" only through
 * a notification would be a form that appears to do nothing at all. Every
 * refusal is answered in place.
 *
 * The image is the subject, so it gets the room: a plate on the left with the
 * file's facts set beneath it in the monospace face this product keeps for
 * exact values, and the two details it needs on the right.
 */
export function UploadMedia({ onCancel, onUploaded, coverSpec, initialFile }: UploadMediaProps) {
  const fileInput = useRef<HTMLInputElement>(null)
  const upload = useUploadMedia()

  const [chosen, setChosen] = useState<Chosen | null>(null)
  const [name, setName] = useState('')
  const [tags, setTags] = useState<string[]>([])
  const [dragging, setDragging] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [crop, setCrop] = useState<CropRect | null>(null)
  const [preparing, setPreparing] = useState(false)

  const accept = useCallback((file: File | undefined) => {
    if (!file) return

    if (!file.type.startsWith('image/')) {
      setError('That file is not an image. Choose a PNG, JPG or GIF.')
      return
    }
    // The original accepted any size and let the request fail server-side.
    if (file.size > MAX_UPLOAD_BYTES) {
      setError(`That image is ${formatBytes(file.size)}. The limit is 8 MB.`)
      return
    }

    const reader = new FileReader()
    reader.onerror = () => setError('That file could not be read. Try another.')
    reader.onload = (event) => {
      const preview = typeof event.target?.result === 'string' ? event.target.result : null
      if (!preview) {
        setError('That file could not be read. Try another.')
        return
      }
      // Dimensions are part of what makes a cover usable, so they are read here
      // rather than left to be discovered after it is on a giveaway.
      const image = new Image()
      image.onload = () => {
        setError(null)
        setChosen({ file, preview, width: image.naturalWidth, height: image.naturalHeight })
        setName((current) => current || file.name.replace(/\.[^.]+$/, ''))
      }
      image.onerror = () => setError('That image could not be decoded. Try another.')
      image.src = preview
    }
    reader.readAsDataURL(file)
  }, [])

  // Pasting a screenshot is how a lot of cover art arrives; the original had no
  // path for it at all.
  useEffect(() => {
    const onPaste = (event: ClipboardEvent) => {
      const file = Array.from(event.clipboardData?.files ?? [])[0]
      if (file) {
        event.preventDefault()
        accept(file)
      }
    }
    window.addEventListener('paste', onPaste)
    return () => window.removeEventListener('paste', onPaste)
  }, [accept])

  useEffect(() => {
    if (initialFile) accept(initialFile)
  }, [initialFile, accept])

  const onDrop = (event: DragEvent<HTMLDivElement>) => {
    event.preventDefault()
    setDragging(false)
    accept(event.dataTransfer.files?.[0])
  }

  const submit = async () => {
    if (!chosen) {
      setError('Choose an image first.')
      return
    }
    let file = chosen.file
    let base64 = chosen.preview
    // Only re-encode when the crop actually cuts something off.
    if (coverSpec && crop && !coversWholeImage(crop, chosen, coverSpec.recommended.width)) {
      setPreparing(true)
      try {
        const cropped = await cropImageToFile(chosen.preview, crop, coverSpec, chosen.file)
        file = cropped.file
        base64 = cropped.dataUrl
      } catch {
        setError('The image could not be cropped. Try another.')
        return
      } finally {
        setPreparing(false)
      }
    }
    try {
      const media = await upload.mutateAsync({
        file,
        name: name.trim() || chosen.file.name,
        tags,
        file_base64: base64,
      })
      onUploaded(media)
    } catch {
      // The mutation cache records it for the notifications page; this keeps
      // the reason in front of the person who has to act on it.
      setError('The upload failed. Check your connection and try again.')
    }
  }

  const busy = upload.isPending || preparing

  // Size of what will actually be uploaded, after cropping and downscaling.
  const output =
    coverSpec && crop
      ? {
          width: Math.round(Math.min(crop.width, coverSpec.recommended.width)),
          height: Math.round(Math.min(crop.width, coverSpec.recommended.width) / coverSpec.aspect),
        }
      : null
  const tooSmall = coverSpec && output ? output.width < coverSpec.minimum.width : false

  return (
    <div className="upload-media">
      <div className="upload-head">
        <button type="button" className="upload-back" onClick={onCancel} disabled={busy}>
          <svg viewBox="0 0 16 16" aria-hidden="true">
            <path d="M9.6 3.6 5.2 8l4.4 4.4" />
          </svg>
          Library
        </button>
        <h2 className="upload-title">Upload an image</h2>
      </div>

      <div className="upload-layout">
        <div className="upload-plate-column">
          <input
            ref={fileInput}
            hidden
            type="file"
            accept="image/*"
            onChange={(event) => {
              accept(event.target.files?.[0])
              // Reset, so choosing the same file twice still fires a change.
              event.target.value = ''
            }}
          />

          {/* A div rather than a button: once an image is on it, it holds its
              own controls, and a button cannot contain buttons. */}
          <div
            className={cn(
              'upload-plate',
              chosen && 'filled',
              dragging && 'dragging',
              busy && 'busy',
            )}
            onDragOver={(event) => {
              event.preventDefault()
              setDragging(true)
            }}
            onDragLeave={() => setDragging(false)}
            onDrop={onDrop}
          >
            {chosen && coverSpec ? (
              <ImageCropper
                key={chosen.preview}
                src={chosen.preview}
                naturalWidth={chosen.width}
                naturalHeight={chosen.height}
                aspect={coverSpec.aspect}
                onChange={setCrop}
                disabled={busy}
              />
            ) : chosen ? (
              <img className="upload-preview" src={chosen.preview} alt="" />
            ) : (
              <button
                type="button"
                className="upload-empty"
                onClick={() => fileInput.current?.click()}
              >
                <svg viewBox="0 0 32 32" className="upload-glyph" aria-hidden="true">
                  <path d="M16 21V7M10.5 12.5 16 7l5.5 5.5" />
                  <path d="M6 20v3.5A1.5 1.5 0 0 0 7.5 25h17a1.5 1.5 0 0 0 1.5-1.5V20" />
                </svg>
                <span className="upload-empty-title">
                  {dragging ? 'Drop it here' : 'Drop an image here'}
                </span>
                <span className="upload-empty-body">or choose a file · or paste one</span>
                {coverSpec ? (
                  <span className="upload-empty-body">
                    Best at {coverSpec.recommended.width}×{coverSpec.recommended.height} (
                    {coverSpec.ratioLabel})
                  </span>
                ) : null}
              </button>
            )}
          </div>

          <div className="upload-facts">
            {chosen ? (
              <>
                <span className="upload-fact-name" title={chosen.file.name}>
                  {chosen.file.name}
                </span>
                <span className="upload-fact-sep">·</span>
                <span>
                  {chosen.width}×{chosen.height}
                  {output && (output.width !== chosen.width || output.height !== chosen.height)
                    ? ` → ${output.width}×${output.height}`
                    : ''}
                </span>
                <span className="upload-fact-sep">·</span>
                <span>{formatBytes(chosen.file.size)}</span>
                <button
                  type="button"
                  className="upload-replace"
                  onClick={() => fileInput.current?.click()}
                  disabled={busy}
                >
                  Replace
                </button>
              </>
            ) : (
              <span>PNG · JPG · GIF — up to 8 MB</span>
            )}
          </div>
          {coverSpec && chosen ? (
            <p className={cn('upload-crop-hint', tooSmall && 'warn')}>
              {tooSmall
                ? `This crop is only ${output?.width}×${output?.height} and may look blurry. Zoom out or use an image of at least ${coverSpec.recommended.width}×${coverSpec.recommended.height}.`
                : `Drag to reposition, scroll to zoom. Covers are shown at ${coverSpec.ratioLabel}.`}
            </p>
          ) : null}
        </div>

        <div className="upload-details">
          <label className="upload-field">
            <span className="upload-field-label">Name</span>
            <input
              className="upload-input"
              placeholder="Cover art"
              value={name}
              onChange={(event) => setName(event.target.value)}
              disabled={busy}
            />
            <span className="upload-field-hint">What you will recognise it by in the library.</span>
          </label>

          <div className="upload-field">
            <span className="upload-field-label">Tags</span>
            <TagEditor tags={tags} onChange={setTags} label="tag" />
            <span className="upload-field-hint">
              Tags are how you filter the library once it gets long.
            </span>
          </div>

          {error ? (
            <p className="upload-error" role="alert">
              {error}
            </p>
          ) : null}

          <div className="upload-actions">
            <button
              type="button"
              className="account-primary btn-sheen"
              onClick={submit}
              disabled={busy || !chosen}
            >
              {busy ? <span className="spinner-inline" aria-hidden="true" /> : null}
              {preparing ? 'Cropping…' : busy ? 'Uploading…' : 'Add to library'}
            </button>
            <button type="button" className="upload-cancel" onClick={onCancel} disabled={busy}>
              Cancel
            </button>
          </div>
        </div>
      </div>
    </div>
  )
}
